import { getDoc, doc, setDoc, updateDoc, deleteField, FieldPath, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase';
import { createLogger } from '../../utils/logger.ts';

// ========================================
// Logger 설정 - BudgetFirebaseService
// ========================================
const logger = createLogger('BudgetFirebaseService');
const DEBUG = false;

// 예산 정보 가져오기 (깊은 복사로 참조 독립성 보장)
export const getBudgetData = async () => {
  try {
    const budgetDocRef = doc(db, 'budget', 'currentBudget');
    const budgetDoc = await getDoc(budgetDocRef);

    if (budgetDoc.exists()) {
      const data = budgetDoc.data();
      return JSON.parse(JSON.stringify(data));
    }
    return null;
  } catch (error) {
    DEBUG && logger.error('예산 데이터 로드 실패:', error);
    return null;
  }
};

// (제거됨) saveBudgetData 통저장(문서 전체 read-modify-write) — per-month 저장 API 전환 완료 후
// 무호출 확인되어 삭제. 월 데이터는 saveBudgetMonth/replaceBudgetMonth 계열만 사용할 것 (코드리뷰 R3 #8).

// ========================================
// per-month 저장 API (코드리뷰 #7 — 크로스-월 유실 방지)
//
// budget/currentBudget 문서 하나에 모든 달이 monthlyData(맵)로 들어있다.
// setDoc(ref, {monthlyData:{[ym]:patch}}, {merge:true})는 map을 재귀 병합하므로
// '그 달만' 갱신하고 다른 달은 서버 값 그대로 보존한다 → 서로 다른 달 동시저장 시
// 유실이 발생하지 않는다. (같은 달 동시저장은 여전히 last-write-wins — 범위 밖)
// ========================================

const BUDGET_DOC_PATH = ['budget', 'currentBudget'] as const;

/**
 * 전역 top-level 팀맵 필드 목록 — 팀 삭제/정규화로 키가 제거될 수 있어
 * 저장 시 '통째 replace'(옛 키 삭제)가 필요한 맵들. replaceMapFields 인자에 그대로 사용.
 * (replaceFields는 맵 전용 — 스칼라는 mergePatch로 넘길 것)
 */
export const GLOBAL_TEAM_MAP_FIELDS = [
  'teamTargetSales',
  'teamPersonnelPercentages',
  'teamIncentiveRatios',
  'teamSalaryTotals',
  'teamIncentiveCalculations',
  'teamPerformanceData',
  'teamPureTargetSales',
] as const;

/**
 * undefined 필드 제거(Firestore는 ignoreUndefinedProperties 미설정 시 undefined를 거부).
 * null/값은 보존. 배열은 통째 교체 대상이나, 원소 객체 내부의 undefined는 재귀 제거한다.
 */
const cleanMonthPatch = (obj: any): any => {
  if (Array.isArray(obj)) return obj.map(cleanMonthPatch);
  if (obj && typeof obj === 'object') {
    const out: any = {};
    for (const [k, v] of Object.entries(obj)) {
      if (v === undefined) continue;
      out[k] = cleanMonthPatch(v);
    }
    return out;
  }
  return obj;
};

/**
 * 특정 월의 필드만 부분 병합 저장. 다른 달·해당 달의 다른 필드 무손상.
 * 주의: '실제로 바뀐 필드만' 넘길 것 (getSafeMonthlyData 기본값 전체를 넘기면
 * 서버에 기본값을 덮어써 월별 동결 패턴을 깰 수 있음). 배열(employees/teams)은 통째 교체.
 * merge 특성상 '빠진 키 삭제'는 안 됨 — 팀맵 키 제거가 필요하면 saveBudgetMonthReplace 사용.
 */
export const saveBudgetMonth = async (yearMonth: string, monthPatch: any): Promise<void> => {
  const ref = doc(db, ...BUDGET_DOC_PATH);
  await setDoc(ref, { monthlyData: { [yearMonth]: cleanMonthPatch(monthPatch) } }, { merge: true });
};

/**
 * 특정 월에서 일부 맵/필드를 '통째 교체'(빠진 키 제거)해야 할 때.
 * mergePatch는 병합, replaceFields의 각 필드는 dot-path로 통째 대체(옛 키 삭제).
 * 값이 undefined인 replaceField는 deleteField()로 필드 자체 삭제.
 * (예: 팀 비활성화 후 teamTargetSales 등에서 옛 팀 키 제거)
 */
export const saveBudgetMonthReplace = async (
  yearMonth: string,
  mergePatch: any,
  replaceFields: Record<string, any>
): Promise<void> => {
  const ref = doc(db, ...BUDGET_DOC_PATH);
  const hasMerge = mergePatch && Object.keys(mergePatch).length > 0;
  const entries = Object.entries(replaceFields || {});
  if (!hasMerge && entries.length === 0) return;

  // merge(부분병합)와 replace(dot-path 통째교체)를 단일 배치로 원자적으로 쓴다(부분 적용 방지).
  const batch = writeBatch(db);
  if (hasMerge) {
    batch.set(ref, { monthlyData: { [yearMonth]: cleanMonthPatch(mergePatch) } }, { merge: true });
  }
  for (const [field, value] of entries) {
    // replaceFields 값도 중첩 undefined를 정리한 뒤 써야 함(Firestore가 undefined 거부).
    // undefined 값은 '필드 삭제' 의도이므로 deleteField()로 치환.
    batch.update(ref, new FieldPath('monthlyData', yearMonth, field), value === undefined ? deleteField() : cleanMonthPatch(value));
  }
  try {
    await batch.commit();
  } catch (e: any) {
    // 문서 부재(not-found)일 때만 병합 생성으로 폴백(신규 문서엔 제거할 옛 키가 없음).
    // 그 외 오류(권한 등)는 전파해 삭제 시맨틱이 조용히 병합으로 격하되지 않도록 한다.
    if (e?.code !== 'not-found') throw e;
    const fallback: any = hasMerge ? { ...cleanMonthPatch(mergePatch) } : {};
    for (const [field, value] of entries) if (value !== undefined) fallback[field] = cleanMonthPatch(value);
    await setDoc(ref, { monthlyData: { [yearMonth]: fallback } }, { merge: true });
  }
};

/**
 * 전역(비월별) top-level 필드만 저장. monthlyData는 절대 포함하지 않는다.
 * replaceMapFields에 지정한 맵 필드는 dot-path로 통째 대체(옛 팀 키 제거) —
 * teamTargetSales/teamIncentiveRatios처럼 정규화로 키가 삭제되는 전역 맵에 사용.
 */
export const saveBudgetGlobal = async (
  globalFields: any,
  replaceMapFields: string[] = []
): Promise<void> => {
  const ref = doc(db, ...BUDGET_DOC_PATH);
  const replaceSet = new Set(replaceMapFields);
  const mergePart: any = {};
  const replacePairs: Array<[string, any]> = [];
  // 🔧 clean 전에 분리: replace 필드의 undefined는 '필드 삭제' 의도이므로
  //    cleanMonthPatch가 키를 지우기 전에 deleteField() 경로로 라우팅되도록 한다.
  for (const [k, v] of Object.entries(globalFields || {})) {
    if (k === 'monthlyData') continue; // 안전장치: 월 데이터 유입 차단
    if (replaceSet.has(k)) replacePairs.push([k, v === undefined ? undefined : cleanMonthPatch(v)]);
    else if (v !== undefined) mergePart[k] = cleanMonthPatch(v);
  }
  const hasMerge = Object.keys(mergePart).length > 0;
  if (!hasMerge && replacePairs.length === 0) return;

  // 전역 merge와 팀맵 replace를 단일 배치로 원자적으로 쓴다.
  const batch = writeBatch(db);
  if (hasMerge) batch.set(ref, mergePart, { merge: true });
  for (const [k, v] of replacePairs) batch.update(ref, new FieldPath(k), v === undefined ? deleteField() : v);
  try {
    await batch.commit();
  } catch (e: any) {
    // 문서 부재(not-found)일 때만 병합 폴백. 그 외 오류는 전파.
    if (e?.code !== 'not-found') throw e;
    const fallback: any = { ...mergePart };
    for (const [k, v] of replacePairs) if (v !== undefined) fallback[k] = v;
    if (Object.keys(fallback).length > 0) await setDoc(ref, fallback, { merge: true });
  }
};

/**
 * 급여 저장용: '소유 필드 한정' 월 저장 + 전역 동기화를 단일 배치로 커밋.
 * 월 통째 교체(replaceBudgetMonth)와 달리, monthMergePatch/monthReplaceFields에 명시한
 * 필드만 건드리고 나머지(doctorTeamMapping·actualSales 캐시·totalActualSales 등
 * 다른 파이프라인 소유 필드)는 영원히 보존한다. — 2026-06 의사매핑 소실 사고 재발 방지.
 * monthReplaceFields: dot-path 통째 교체(옛 키 삭제), 값 undefined는 필드 삭제.
 */
export const saveBudgetMonthFieldsAndGlobal = async (
  yearMonth: string,
  monthMergePatch: any,
  monthReplaceFields: Record<string, any>,
  globalFields: any = null,
  globalReplaceMapFields: string[] = []
): Promise<void> => {
  const ref = doc(db, ...BUDGET_DOC_PATH);
  const cleanedMonthMerge = cleanMonthPatch(monthMergePatch || {});
  const hasMonthMerge = Object.keys(cleanedMonthMerge).length > 0;

  // 전역 필드 분리 (clean 전에 — replace 필드의 undefined는 '삭제' 의도)
  const gReplaceSet = new Set(globalReplaceMapFields);
  const gMerge: any = {};
  const gReplace: Array<[string, any]> = [];
  for (const [k, v] of Object.entries(globalFields || {})) {
    if (k === 'monthlyData') continue; // 안전장치
    if (gReplaceSet.has(k)) gReplace.push([k, v === undefined ? undefined : cleanMonthPatch(v)]);
    else if (v !== undefined) gMerge[k] = cleanMonthPatch(v);
  }

  const batch = writeBatch(db);
  const setPayload: any = { ...gMerge };
  if (hasMonthMerge) setPayload.monthlyData = { [yearMonth]: cleanedMonthMerge };
  if (Object.keys(setPayload).length > 0) batch.set(ref, setPayload, { merge: true });
  for (const [f, v] of Object.entries(monthReplaceFields || {})) {
    batch.update(ref, new FieldPath('monthlyData', yearMonth, f), v === undefined ? deleteField() : cleanMonthPatch(v));
  }
  for (const [k, v] of gReplace) {
    batch.update(ref, new FieldPath(k), v === undefined ? deleteField() : v);
  }
  try {
    await batch.commit();
  } catch (e: any) {
    // 문서 부재(not-found)일 때만 병합 생성 폴백. 그 외 오류는 전파.
    if (e?.code !== 'not-found') throw e;
    const monthObj: any = { ...cleanedMonthMerge };
    for (const [f, v] of Object.entries(monthReplaceFields || {})) if (v !== undefined) monthObj[f] = cleanMonthPatch(v);
    const fallback: any = { ...gMerge };
    if (Object.keys(monthObj).length > 0) fallback.monthlyData = { [yearMonth]: monthObj };
    for (const [k, v] of gReplace) if (v !== undefined) fallback[k] = v;
    if (Object.keys(fallback).length > 0) await setDoc(ref, fallback, { merge: true });
  }
};

/**
 * 특정 월의 의사-팀 매핑 upsert (한 개 키 추가/수정).
 */
export const saveDoctorMappingField = async (
  yearMonth: string,
  doctorName: string,
  teamName: string
): Promise<void> => {
  const ref = doc(db, ...BUDGET_DOC_PATH);
  await setDoc(
    ref,
    { monthlyData: { [yearMonth]: { doctorTeamMapping: { [doctorName]: teamName } } } },
    { merge: true }
  );
};

/**
 * 특정 월의 의사-팀 매핑에서 한 키 삭제.
 */
export const deleteDoctorMapping = async (yearMonth: string, doctorName: string): Promise<void> => {
  const ref = doc(db, ...BUDGET_DOC_PATH);
  try {
    await updateDoc(ref, new FieldPath('monthlyData', yearMonth, 'doctorTeamMapping', doctorName), deleteField());
  } catch (e: any) {
    // 문서/필드 부재(not-found)는 정상 no-op. 그 외 오류(권한 등)는 전파해
    // 호출부가 '삭제 저장 완료'로 오인하지 않도록 한다.
    if (e?.code !== 'not-found') throw e;
    DEBUG && logger.warn('deleteDoctorMapping no-op (문서/필드 부재)');
  }
};

/**
 * 특정 월의 의사-팀 매핑 전체 교체(빠진 키 제거).
 */
export const replaceDoctorMapping = async (
  yearMonth: string,
  mapping: Record<string, string>
): Promise<void> => {
  const ref = doc(db, ...BUDGET_DOC_PATH);
  try {
    await updateDoc(ref, new FieldPath('monthlyData', yearMonth, 'doctorTeamMapping'), mapping || {});
  } catch (e: any) {
    // 문서 부재(not-found)일 때만 병합 생성으로 폴백(신규 문서엔 제거할 옛 키가 없음).
    // 그 외 오류는 전파해 '통째 교체'가 조용히 병합으로 격하되지 않도록 한다.
    if (e?.code !== 'not-found') throw e;
    await setDoc(
      ref,
      { monthlyData: { [yearMonth]: { doctorTeamMapping: mapping || {} } } },
      { merge: true }
    );
  }
};

// ⚠️ replaceBudgetMonth / replaceBudgetMonthAndGlobal (월 '통째 교체' API)는 2026-06 사고
// (다른 파이프라인 소유 필드 doctorTeamMapping·teamActualSales 소실)의 원인 패턴이라 삭제됨.
// 월 저장은 saveBudgetMonth(병합)·saveBudgetMonthReplace(필드 단위 교체)·
// saveBudgetMonthFieldsAndGlobal(필드 단위 교체 + 전역 동기화)만 사용할 것.
